import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Plus, Upload, Trash2, Eye, EyeOff, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CouponsTab = ({
  coupons,
  newCoupon,
  setNewCoupon,
  handleCreateCoupon,
  handleDeleteCoupon,
  hasPermission
}) => {
  const [uploading, setUploading] = useState(false);

  const uploadCouponImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/upload-image`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        const imageUrl = data.url || data.image_url || data.file_url;
        if (imageUrl) setNewCoupon({ ...newCoupon, creative_image_url: imageUrl });
      }
    } catch (err) { console.error('Image upload failed:', err); }
    finally { setUploading(false); }
  };

  const toggleShowOnWebsite = async (coupon) => {
    try {
      const newVal = !coupon.show_on_website;
      await fetch(`${API_URL}/api/admin/coupons/${coupon.code}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_on_website: newVal })
      });
      // Trigger parent refresh
      if (handleDeleteCoupon) {
        // Hacky but works - the parent fetchCoupons is called via the same refresh mechanism
        window.location.reload();
      }
    } catch (err) { console.error('Toggle failed:', err); }
  };

  return (
    <div className="space-y-6">
      {/* Create New Coupon */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            Create New Coupon
          </CardTitle>
          <CardDescription className="text-gray-600">Create discount coupons for various events and promotions</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateCoupon} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="code" className="text-gray-700">Coupon Code</Label>
              <Input id="code" value={newCoupon.code} onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} placeholder="DIWALI50" className="border-gray-300" required />
            </div>
            <div>
              <Label htmlFor="discount_type" className="text-gray-700">Discount Type</Label>
              <select id="discount_type" value={newCoupon.discount_type} onChange={(e) => setNewCoupon({...newCoupon, discount_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount</option>
              </select>
            </div>
            <div>
              <Label htmlFor="discount_value" className="text-gray-700">{newCoupon.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}</Label>
              <Input id="discount_value" type="number" value={newCoupon.discount_value} onChange={(e) => setNewCoupon({...newCoupon, discount_value: e.target.value})} placeholder={newCoupon.discount_type === 'percentage' ? '10' : '500'} className="border-gray-300" required />
            </div>
            <div>
              <Label htmlFor="usage_limit" className="text-gray-700">Usage Limit</Label>
              <Input id="usage_limit" type="number" value={newCoupon.usage_limit} onChange={(e) => setNewCoupon({...newCoupon, usage_limit: e.target.value})} placeholder="100" className="border-gray-300" />
            </div>
            <div>
              <Label htmlFor="min_order_amount" className="text-gray-700">Min Order Amount (₹)</Label>
              <Input id="min_order_amount" type="number" value={newCoupon.min_order_amount} onChange={(e) => setNewCoupon({...newCoupon, min_order_amount: e.target.value})} placeholder="0" className="border-gray-300" />
            </div>
            <div>
              <Label htmlFor="valid_until" className="text-gray-700">Valid Until</Label>
              <Input id="valid_until" type="datetime-local" value={newCoupon.valid_until} onChange={(e) => setNewCoupon({...newCoupon, valid_until: e.target.value})} className="border-gray-300" />
            </div>

            {/* Offer Display Fields */}
            <div>
              <Label className="text-gray-700">Offer Title (shown on website)</Label>
              <Input value={newCoupon.offer_title || ''} onChange={(e) => setNewCoupon({...newCoupon, offer_title: e.target.value})} placeholder="Diwali Special Offer!" className="border-gray-300" />
            </div>
            <div>
              <Label className="text-gray-700">Offer Description</Label>
              <Input value={newCoupon.offer_description || ''} onChange={(e) => setNewCoupon({...newCoupon, offer_description: e.target.value})} placeholder="Get 50% off on all plans" className="border-gray-300" />
            </div>

            {/* Image Upload */}
            <div className="md:col-span-2">
              <Label className="text-gray-700 mb-2 block">Coupon Banner Image</Label>
              <div className="flex items-center gap-4">
                {newCoupon.creative_image_url ? (
                  <div className="relative w-48 h-24 rounded-lg overflow-hidden border border-gray-200">
                    <img src={newCoupon.creative_image_url.startsWith('http') ? newCoupon.creative_image_url : `${API_URL}${newCoupon.creative_image_url}`} alt="Coupon banner" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setNewCoupon({...newCoupon, creative_image_url: ''})} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
                    <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadCouponImage(e.target.files[0]); }} />
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Upload Banner Image'}</span>
                  </label>
                )}
              </div>
            </div>

            {/* Show on Website Toggle */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Switch
                  checked={newCoupon.show_on_website || false}
                  onCheckedChange={(checked) => setNewCoupon({...newCoupon, show_on_website: checked})}
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">Show on Website</p>
                  <p className="text-xs text-gray-500">When enabled, this coupon will be visible to visitors on the landing page</p>
                </div>
              </div>
            </div>

            {/* Applicable Services */}
            <div className="md:col-span-2">
              <Label className="text-gray-700 mb-2 block">Applicable Services *</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {[
                  { value: 'options_only', label: 'Options Only (Monthly)' },
                  { value: 'options_only_3month', label: 'Options Only (Quarterly)' },
                  { value: 'complete_package', label: 'Complete Package (Monthly)' },
                  { value: 'complete_package_3month', label: 'Complete Package (Quarterly)' },
                  { value: 'equity_only', label: 'Equity Only (Monthly)' },
                  { value: 'equity_only_3month', label: 'Equity Only (Quarterly)' },
                  { value: 'workshop', label: 'Workshop' },
                  { value: 'index_options_educational', label: 'Index Options (Educational)' },
                  { value: 'automated_alerts_1day', label: 'Alerts (1 Day)' },
                  { value: 'automated_alerts_3day', label: 'Alerts (3 Day)' },
                  { value: 'automated_alerts_5day', label: 'Alerts (5 Day)' }
                ].map((service) => (
                  <label key={service.value} className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={newCoupon.applicable_subscription_types.includes(service.value)} onChange={(e) => {
                      const types = e.target.checked
                        ? [...newCoupon.applicable_subscription_types, service.value]
                        : newCoupon.applicable_subscription_types.filter(t => t !== service.value);
                      setNewCoupon({...newCoupon, applicable_subscription_types: types});
                    }} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <span className="text-sm text-gray-700">{service.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Select at least one service for this coupon</p>
            </div>

            <div className="md:col-span-2">
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Coupon</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing Coupons */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900">Existing Coupons</CardTitle>
          <CardDescription className="text-gray-600">All created discount coupons</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Code</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Discount</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Usage / Left</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Applicable Services</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-700">Visible</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Valid Until</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon, index) => {
                  const remaining = coupon.usage_limit ? coupon.usage_limit - (coupon.usage_count || 0) : null;
                  return (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          {coupon.creative_image_url && (
                            <img
                              src={coupon.creative_image_url.startsWith('http') ? coupon.creative_image_url : `${API_URL}${coupon.creative_image_url}`}
                              alt="" className="w-8 h-8 rounded object-cover"
                            />
                          )}
                          <div>
                            <code className="bg-gray-100 px-2 py-1 rounded text-gray-900 font-medium">{coupon.code}</code>
                            {coupon.offer_title && <p className="text-[10px] text-gray-500 mt-0.5">{coupon.offer_title}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="border-green-300 text-green-700">
                          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-gray-700">{coupon.usage_count || 0}/{coupon.usage_limit || '∞'}</span>
                        {remaining !== null && (
                          <Badge className={`ml-1.5 text-[10px] ${remaining > 10 ? 'bg-green-50 text-green-700' : remaining > 0 ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}>
                            {remaining > 0 ? `${remaining} left` : 'Exhausted'}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {(coupon.applicable_subscription_types && coupon.applicable_subscription_types.length > 0) ? (
                          <div className="flex flex-wrap gap-1">
                            {coupon.applicable_subscription_types.map((type, idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px] border-blue-200 text-blue-700">{type}</Badge>
                            ))}
                          </div>
                        ) : <span className="text-gray-400 text-xs">All services</span>}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => toggleShowOnWebsite(coupon)}
                          className={`p-1.5 rounded-md transition-colors ${coupon.show_on_website ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                          title={coupon.show_on_website ? 'Visible on website - click to hide' : 'Hidden - click to show on website'}
                        >
                          {coupon.show_on_website ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant={coupon.status === 'active' ? 'default' : 'secondary'}
                          className={coupon.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {coupon.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-gray-600">
                        {coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString() : 'No expiry'}
                      </td>
                      <td className="py-2 px-3">
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteCoupon(coupon.code)} className="text-xs">Delete</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CouponsTab;
